import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getPropuestas, enviarAMeta, devolverPropuesta } from '../../../services/kapsoTemplatesService';

/**
 * La cola de ClaimUp: las plantillas que han propuesto los clientes.
 *
 * Revisarla antes de Meta es lo que protege el número del cliente: una plantilla
 * mal escrita le baja la calidad de la cuenta. **Aprobar es mandarla a Meta**;
 * devolverla lleva un motivo, que es lo que el cliente lee para corregirla.
 * Ver docs/ar-plantillas-propuestas-plan.md.
 */
const Propuesta = ({ p, onHecho }) => {
  const [devolviendo, setDevolviendo] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const hacer = async (fn, ok) => {
    setOcupado(true);
    try {
      await fn();
      toast.success(ok);
      onHecho();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setOcupado(false);
    }
  };

  return (
    <div className='rounded-lg border border-slate-200 bg-white p-4'>
      <div className='flex flex-wrap items-center gap-2'>
        <span className='font-medium text-slate-800'>{p.description || p.key}</span>
        <span className='text-sm text-slate-500'>· {p.company_name || p.company_id}</span>
      </div>
      <p className='mt-2 whitespace-pre-wrap text-sm text-slate-600'>{p.preview_text}</p>
      {p.unknown_variables?.length > 0 && (
        <p className='mt-2 text-sm text-red-700'>
          Usa variables que no sabemos rellenar: {p.unknown_variables.map(v => `{{${v}}}`).join(', ')}
        </p>
      )}

      {devolviendo ? (
        <div className='mt-3 space-y-2'>
          <textarea
            aria-label='Motivo para el cliente'
            value={motivo}
            onChange={e => setMotivo(e.target.value)}
            rows={2}
            placeholder='Qué tiene que cambiar, para que lo corrija'
            className='block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm'
          />
          <div className='flex gap-2'>
            <button
              type='button'
              disabled={ocupado || motivo.trim().length < 3}
              onClick={() =>
                hacer(() => devolverPropuesta(p.company_id, p.key, motivo.trim()), 'Devuelta al cliente')
              }
              className='rounded-lg bg-orange-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50'
            >
              Devolver
            </button>
            <button
              type='button'
              onClick={() => setDevolviendo(false)}
              className='rounded-lg px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50'
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <div className='mt-3 flex gap-2'>
          <button
            type='button'
            disabled={ocupado}
            onClick={() => hacer(() => enviarAMeta(p.company_id, p.key), 'Enviada a Meta para su revisión')}
            className='rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50'
          >
            Aprobar y mandar a Meta
          </button>
          <button
            type='button'
            onClick={() => setDevolviendo(true)}
            className='rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50'
          >
            Devolver al cliente
          </button>
        </div>
      )}
    </div>
  );
};

const KapsoProposals = () => {
  const [propuestas, setPropuestas] = useState([]);

  // Solo la cola del TEXTO. La de orden desapareció el 21/10/2026: el cliente
  // ordena su escalera al momento y lo que la protege son dos topes del
  // servidor (docs/escalera-con-topes-plan.md).
  const cargar = useCallback(async () => {
    try {
      setPropuestas(await getPropuestas());
    } catch (err) {
      toast.error(err.message);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  if (propuestas.length === 0) {
    return <p className='text-sm text-slate-500'>No hay propuestas de clientes pendientes.</p>;
  }

  return (
    <div className='space-y-3'>
      {propuestas.map(p => (
        <Propuesta key={`${p.company_id}-${p.key}`} p={p} onHecho={cargar} />
      ))}

    </div>
  );
};

export default KapsoProposals;
